// authService.ts - Authentication Service for Non-Keycloak Mode

import apiClient from './apiClient';
import { closeAllRtuWebSessions } from './rtuWebGatewaySessions';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  type: string;
  capabilities?: string[];
  user: {
    employeeId: number;
    firstName: string;
    lastName: string;
    logins: string;
    email?: string;
    // Add other employee fields as needed
  };
}

export interface AuthUser {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email?: string;
  roles: string[];
  capabilities: string[];
}

interface LogoutOptions {
  redirectToLogin?: boolean;
  reason?: string;
}

class AuthService {
  private readonly TOKEN_KEY = 'dms_auth_token';
  private readonly USER_KEY = 'dms_auth_user';
  private readonly TOKEN_EXPIRY_SKEW_MS = 5000;

  /**
   * Login with username and password
   */
  async login(credentials: LoginRequest): Promise<AuthUser> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
      
      if (response.data.token) {
        const authUser = this.mapToAuthUser(response.data.user, response.data.capabilities);
        // Store token and user data
        this.setToken(response.data.token);
        this.setUser(authUser);
        
        console.log('🔐 Login successful:', response.data.user);
        return authUser;
      }
      
      throw new Error('No token received from server');
    } catch (error: any) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data || 'Login failed');
    }
  }

  /**
   * Logout user
   */
  async logout(options: LogoutOptions = {}): Promise<void> {
    try {
      this.clearAuth();
      this.broadcastLogout();
      if (options.redirectToLogin) {
        this.redirectToLogin();
      }
      console.log('🔓 Logged out successfully', options.reason ? `(${options.reason})` : '');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getValidToken(true);
  }

  /**
   * Get current user
   */
  getCurrentUser(): AuthUser | null {
    if (!this.getValidToken(false)) {
      return null;
    }
    const userData = localStorage.getItem(this.USER_KEY);
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return this.mapToAuthUser(user);
      } catch (error) {
        console.error('Error parsing user data:', error);
        this.clearAuth();
      }
    }
    return null;
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getValidToken(redirectToLoginOnFailure = true): string | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    if (!this.isTokenValid(token)) {
      this.handleAuthFailure({
        redirectToLogin: redirectToLoginOnFailure,
        reason: 'token-invalid-or-expired',
      });
      return null;
    }

    return token;
  }

  /**
   * Set authentication token
   */
  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Set user data
   */
  private setUser(user: any): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Clear authentication data
   */
  private clearAuth(): void {
    // Close RTU web sessions with the outgoing token before it is removed (best effort, never throws).
    closeAllRtuWebSessions('DMS_SIGNED_OUT', localStorage.getItem(this.TOKEN_KEY));
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    // Also emit a storage event fallback for same-tab listeners
    try {
      localStorage.setItem('dms_auth_last_clear_ts', Date.now().toString());
      localStorage.removeItem('dms_auth_last_clear_ts');
    } catch {}
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return null;
    }

    try {
      const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padding = '='.repeat((4 - (base64Payload.length % 4)) % 4);
      const decoded = atob(base64Payload + padding);
      return JSON.parse(decoded) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private isTokenExpired(payload: Record<string, unknown>): boolean {
    const expClaim = payload.exp;
    if (expClaim === undefined || expClaim === null) {
      return true;
    }

    const expSeconds = typeof expClaim === 'number' ? expClaim : Number(expClaim);
    if (!Number.isFinite(expSeconds)) {
      return true;
    }

    return expSeconds * 1000 <= Date.now() + this.TOKEN_EXPIRY_SKEW_MS;
  }

  /**
   * Validate token format and expiry (if exp claim is present)
   */
  private isTokenValid(token: string): boolean {
    const payload = this.decodeJwtPayload(token);
    if (!payload) {
      return false;
    }
    return !this.isTokenExpired(payload);
  }

  private broadcastLogout(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dms-auth-logout'));
    }
  }

  private redirectToLogin(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }

  handleAuthFailure(options: LogoutOptions = {}): void {
    this.clearAuth();
    this.broadcastLogout();

    if (options.redirectToLogin) {
      this.redirectToLogin();
    }

    if (options.reason) {
      console.warn(`🔓 Authentication invalidated: ${options.reason}`);
    }
  }

  /**
   * Map backend user to frontend AuthUser
   */
  private mapToAuthUser(user: any, capabilities?: string[]): AuthUser {
    const resolvedCapabilities = Array.isArray(capabilities)
      ? capabilities
      : Array.isArray(user?.capabilities)
        ? user.capabilities
        : [];

    const resolvedRoles = Array.isArray(user?.roles) && user.roles.length > 0
      ? user.roles
      : ['user'];

    return {
      id: user.id ?? user.employeeId,
      username: user.username ?? user.logins,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roles: resolvedRoles,
      capabilities: resolvedCapabilities.map((value: unknown) => String(value))
    };
  }

  /**
   * Test authentication endpoint
   */
  async testAuth(): Promise<boolean> {
    try {
      const response = await apiClient.get('/api/auth/test');
      console.log('Auth test result:', response.data);
      return true;
    } catch (error) {
      console.error('Auth test failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
